// The uploaded icons' access gates, out of CustomizerScreen, PlaceOrderScreen and
// ProcessOrderScreen (backfill PR 31): whether a design's uploaded icons are out of reach
// because nobody is signed in, the ids the cart's IndexedDB designs show, the check with
// POST /api/user-icons/render-assets that the signed-in account can load every uploaded
// icon a design shows (before the customizer's exports and its add to cart, and before the
// checkout starts PayPal), and the render assets the order processing can't print with.
import api from '../api';
import { toast } from 'react-toastify';
import { toastRequestError } from '../utils';
import { collectUserIconAssetIds } from './userIconAssets';
import { getMigratedDesignAssetFromIndexedDB } from './customizerCompat';

export const uploadedIconsNeedSignIn = (usedUserIconAssetIds, userInfo) => {
  if (userInfo) {
    return false; // if the user is signed in, uploaded icon assets can be fetched normally
  }

  return usedUserIconAssetIds.length > 0; // if the user is signed out and the current design references uploaded icons, those icons are temporarily unavailable
}; // checks whether uploaded icons are unavailable because the user is signed out (the customizer's exports and cart are blocked by it, and it shows the signed-out notice once)

export const cartUserIconAssetIds = async (cartItems) => {
  const allAssetIds = new Set();

  await Promise.all(
    cartItems.map(async (cartItem) => {
      // go through all cart items:
      if (!cartItem.uniqueCartId) {
        return; // skip cart items without a uniqueCartId (only cart items with a uniqueCartId were added directly from the Customizer and only these designs need to be checked for uploaded icons access rights)
      }

      const asset = await getMigratedDesignAssetFromIndexedDB(
        cartItem.uniqueCartId
      ); // get the asset from the indexedDB for the current cartItem

      if (!asset?.design) {
        return;
      }

      collectUserIconAssetIds(asset.design).forEach((assetId) => {
        allAssetIds.add(assetId);
      }); // collect all uploaded icon assetIds that are used inside the current design and add them to allAssetIds
    })
  ); // collect all uploaded icon assetIds from all cart designs stored in IndexedDB

  return [...allAssetIds];
}; // the uploaded icons the cart's customizer designs show, each once

export const missingUserIconAssetIds = (assetIds, renderAssets) => {
  const returnedAssetIds = new Set(
    renderAssets.map((asset) => String(asset.assetId))
  ); // collect all uploaded icon assetIds that were successfully returned by the backend

  return assetIds.filter((assetId) => !returnedAssetIds.has(assetId)); // find referenced uploaded icons that were not returned because they are unavailable for this account, deleted, or invalid
}; // the ids the render-assets route left out of its answer

export const verifyUserIconAssetAccess = async ({
  assetIds,
  userInfo,
  t,
  loginRequiredKey,
  unavailableKey,
}) => {
  if (assetIds.length === 0) {
    return true; // no uploaded icons are used, so no account-specific asset access is needed
  }

  if (!userInfo) {
    toast.info(t(loginRequiredKey));
    return false; // uploaded icons are used, but the user is signed out
  }

  try {
    const { data } = await api.post('/api/user-icons/render-assets', {
      assetIds,
    }); // ask the backend which uploaded icon assets this signed-in user can actually access

    if (missingUserIconAssetIds(assetIds, data).length > 0) {
      toast.error(t(unavailableKey));
      return false; // stop because exporting would create a file with missing uploaded icons
    }

    return true;
  } catch (err) {
    toastRequestError(err, t); // the route's message (a fallback to unavailableKey stood here and never showed: deleted in PR 31, user decision)
    return false; // if uploaded icon access cannot be verified, do not export
  }
}; // whether the signed-in user can access every uploaded icon in assetIds, with the toast that says why not (the customizer's design check and the checkout's cart check held a copy each until PR 31; an admin passes for every icon, as the route sends admins all of them: KNOWN_BUGS.md, the checkout routes)

export const unavailableUserIconAssetIds = (assetIds, assetsById) =>
  assetIds.filter((assetId) => {
    const asset = assetsById[assetId];

    if (!asset) {
      return true; // backend did not return this requested uploaded icon asset
    }

    if (asset.format === 'png') {
      return !asset.iconUrl; // PNG render asset needs a signed URL
    }

    if (asset.format === 'svg') {
      return !asset.svgInnerMarkup; // SVG render asset needs inline markup
    }

    return true; // unknown format is not safe for printing export
  }); // the uploaded icons a printing file can't embed from the cached render assets
